"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import type { AtsLob } from "@/types/ats/ats-sync-settings"
import { useTranslation } from "@/hooks/use-translation"

interface ApplyToAllModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (lobIds: number[]) => void
  lobs: AtsLob[]
  filteredCount: number
  filterChips: string[]
}

export function ApplyToAllModal({ isOpen, onClose, onApply, lobs, filteredCount, filterChips }: ApplyToAllModalProps) {
  const { t } = useTranslation()
  const [selectedLobIds, setSelectedLobIds] = useState<number[]>([])

  useEffect(() => {
    if (isOpen) setSelectedLobIds([])
  }, [isOpen])

  const allState: true | false | "indeterminate" =
    lobs.length > 0 && lobs.every((l) => selectedLobIds.includes(l.id))
      ? true
      : selectedLobIds.length === 0
        ? false
        : "indeterminate"

  const toggleAll = () => {
    setSelectedLobIds(allState === true ? [] : lobs.map((l) => l.id))
  }

  const toggleLob = (lobId: number) => {
    setSelectedLobIds((prev) =>
      prev.includes(lobId) ? prev.filter((id) => id !== lobId) : [...prev, lobId]
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-sm flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-blue-900">{t('atsSyncApplyToAllTitle')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600">
          {t(filteredCount === 1 ? 'atsSyncApplyToAllBodySingular' : 'atsSyncApplyToAllBodyPlural', { count: String(filteredCount) })}
        </p>

        <div className="flex flex-wrap gap-1.5">
          {filterChips.length === 0 ? (
            <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 text-xs px-2.5 py-1">
              {t('atsSyncAllClients')}
            </span>
          ) : (
            filterChips.map((chip) => (
              <span key={chip} className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-xs px-2.5 py-1">
                {chip}
              </span>
            ))
          )}
        </div>

        <div className="flex flex-col gap-2.5">
          <label className="flex items-center gap-2.5 text-sm font-medium text-gray-700 pb-2 border-b border-gray-100 cursor-pointer">
            <Checkbox checked={allState} onCheckedChange={toggleAll} aria-label={t('atsSyncAllLinesOfBusiness')} />
            {t('all')}
          </label>
          {lobs.map((lob) => (
            <label key={lob.id} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
              <Checkbox
                checked={selectedLobIds.includes(lob.id)}
                onCheckedChange={() => toggleLob(lob.id)}
                aria-label={lob.name}
              />
              {lob.name}
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button variant="elliptical-outline" onClick={onClose}>{t('cancel')}</Button>
          <Button
            variant="elliptical"
            onClick={() => { onApply(selectedLobIds); onClose() }}
            disabled={selectedLobIds.length === 0}
          >
            {t('atsSyncApplyToCount', { count: String(filteredCount) })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
